'use client'
import CouponDisplay from '@components/atoms/CouponDisplay'
import { Separator } from '@components/ui/separator'
import { Skeleton } from '@components/ui/skeleton'
import { useSession } from 'next-auth/react'
import { FC, useCallback, useEffect, useState } from 'react'

export interface CouponType {
  id: number
  code: string
  description: string
  discount: number
  expiryDate: string
  minBillingAmount: number
  maxBillingAmount: number
}

export interface CouponDiscount {
  discountAmount: number
  discountedBillingAmount: number
  billingAmount: number
  couponApplied: string
  status: string
}
export type CouponSelectedProp = CouponType & CouponDiscount

export const fetchCoupons = async (amount: number) => {
  const res = await fetch(`/api/coupons/eligibleCoupons?billingAmount=${amount}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    }
  })

  if (!res.ok) {
    throw new Error('failed to fetch coupons')
  }

  const response: { coupons: CouponType[] } = await res.json()
  const tmp = response.coupons.sort((a, b) => b.discount - a.discount)
  return { coupons: [...tmp] }
}

const fetchDiscountedValue = async (couponCode: string | undefined, amount: number) => {
  if (!couponCode) {
    throw new Error('failed to get discounted rates')
  }
  const res = await fetch(`/api/coupons/calcDiscount/${couponCode}?billingAmount=${amount}`)

  if (!res.ok) {
    throw new Error('failed to get discounted rates')
  }

  const response: CouponDiscount = await res.json()
  return response
}

const CouponSelector: FC<{
  amount: number
  onSelectingCoupon?: (selectedCoupon: CouponSelectedProp | undefined) => void
  couponApplied?: boolean
}> = ({ amount, onSelectingCoupon, couponApplied = false }) => {
  const { data: session } = useSession()
  const [isLoading, setIsLoading] = useState(false)
  const [couponData, setCouponData] = useState<undefined | { coupons: CouponType[] }>(undefined)

  const [expanded, setExpanded] = useState(false)
  const [selectedCouponIndex, setSelectedCouponIndex] = useState<number | undefined>(undefined)

  const handleRemoveCoupon = useCallback(() => {
    setSelectedCouponIndex(undefined)
    if (onSelectingCoupon) {
      onSelectingCoupon(undefined)
    }
  }, [onSelectingCoupon])

  const fetchCouponData = useCallback(async () => {
    setIsLoading(true)
    const data = await fetchCoupons(amount)
    if (selectedCouponIndex && data.coupons.length - 1 < selectedCouponIndex) {
      // when user select a bigger coupon and then lower cart balue, reset the selection
      // handleRemoveCoupon()
    }
    setCouponData(data)
    setIsLoading(false)
    return data
  }, [amount, selectedCouponIndex])

  useEffect(() => {
    fetchCouponData()
  }, [fetchCouponData])

  const handleAppyCoupon = useCallback(
    async (index: number) => {
      setExpanded(false)
      setSelectedCouponIndex(index)
      if (onSelectingCoupon && couponData) {
        const discountDets = await fetchDiscountedValue(couponData.coupons[index].code, amount)
        onSelectingCoupon({ ...discountDets, ...couponData.coupons[index] })
      }
    },
    [amount, couponData, onSelectingCoupon]
  )

  const selectTheBiggestCoupon = useCallback(async () => {
    if (couponApplied && couponData?.coupons) {
      const coupons = couponData.coupons
      let highestDiscount = 0
      coupons.map((coupon, ind) => {
        if (coupon.discount > coupons[highestDiscount].discount) {
          highestDiscount = ind
        }
      })
      setSelectedCouponIndex(highestDiscount)
      if (onSelectingCoupon) {
        const discountDets = await fetchDiscountedValue(couponData.coupons[0].code, amount)
        onSelectingCoupon({ ...discountDets, ...couponData.coupons[0] })
      }
    }
  }, [couponApplied, couponData?.coupons, onSelectingCoupon, amount])

  useEffect(() => {
    selectTheBiggestCoupon()
  }, [couponApplied, selectTheBiggestCoupon])

  if (session) {
    return typeof selectedCouponIndex !== 'number' ? (
      <>
        <div>
          {isLoading ? (
            <div>
              <div className="h-16 p-3 flex gap-3 justify-around">
                <Skeleton className=" w-1/2" />
                <Skeleton className=" w-full" />
                <Skeleton className=" w-full" />
              </div>
              <div className="p-3 h-12 flex">
                <Skeleton className=" w-full" />
              </div>
            </div>
          ) : couponData && couponData.coupons.length > 0 ? (
            <>
              <CouponDisplay
                code={couponData.coupons[0].code}
                description={couponData?.coupons[0].description}
                onApply={() => handleAppyCoupon(0)}
                applied={selectedCouponIndex === 0}
                key={couponData.coupons[0].code}
              />
              {couponData.coupons.length > 1 ? (
                // when more than one coupon is available
                expanded ? (
                  <>
                    {couponData.coupons.map((coupon, ind) => {
                      if (ind !== 0) {
                        return (
                          <>
                            <Separator />
                            <CouponDisplay
                              code={coupon.code}
                              description={coupon.description}
                              onApply={() => handleAppyCoupon(ind)}
                              applied={selectedCouponIndex === ind}
                              key={coupon.code}
                            />
                          </>
                        )
                      }
                    })}
                  </>
                ) : (
                  <>
                    {/* by default only one coupon will be shown. On clicking show more */}
                    {/* more coupons will be visible */}
                    <Separator />
                    <div
                      className="text-center heading_small text-primary-500 p-5 cursor-pointer"
                      onClick={() => setExpanded(!expanded)}
                    >
                      View more coupons →
                    </div>
                  </>
                )
              ) : undefined}
            </>
          ) : undefined}
        </div>
      </>
    ) : (
      <div>
        <CouponDisplay
          code={couponData!.coupons[selectedCouponIndex].code}
          description={couponData!.coupons[selectedCouponIndex].description}
          onApply={handleRemoveCoupon}
          applied={selectedCouponIndex === selectedCouponIndex}
          key={'applied-coupon'}
        />
      </div>
    )
  }
}

export default CouponSelector
